import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CitySelect from './CitySelect';
import RouteSelect from './RouteSelect';
import RouteShape from './RouteShape';
import { fetchStopsByRoute, fetchArrivalTimeByRoute } from '../api/busApi';
import Card from 'react-bootstrap/Card';
import './BusTime.less';
import ListGroup from 'react-bootstrap/ListGroup';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import TimeUpdate from './TimeUpdate';
import logo from '../assets/logo.svg';
import caption from '../assets/caption.svg';
import emptyBg from '../assets/bg_empty.jpg';
import { Toast, ToastContainer, Button  } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import BusAlertIcon from '@mui/icons-material/BusAlert';



const BusTime = () => {
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedRouteName, setSelectedRouteName] = useState(null);
  const [currentDirection, setCurrentDirection] = useState(0); 
  const [stopsData, setStopsData] = useState([]);
  const [routeStopsArrivalTimes, setRouteStopsArrivalTimes] = useState([]);

  const handleCityChange = (city) => {
    setSelectedCity(city);
  };

  const handleRouteChange = ({route, routeName}) => {
    setSelectedRoute(route);
    setSelectedRouteName(routeName);
  };

  const fetchStopsData = useCallback(async () => {
    try {
      const response = await fetchStopsByRoute(selectedCity,selectedRoute);
      setStopsData(response.data);
    } catch (error) {
      // api error handler for demo use
      const response = await fetch(`mockResponse/routeStops.json`);
      response.data = await response.json(); 
      setStopsData(response.data);
    }
  }, [selectedCity, selectedRoute]);

  useEffect(() => {
    if (!selectedCity || !selectedRoute) {
      return;
    }
    fetchStopsData();
  }, [selectedRoute, fetchStopsData]);

  const fetchArrivalTimes = useCallback(async () => {
    try {
      const response = await fetchArrivalTimeByRoute(selectedCity, selectedRoute);
      setRouteStopsArrivalTimes(response.data);
    } catch (error) {
       // api error handler for demo use
       const response = await fetch(`mockResponse/arrivalTimes.json`);
       response.data = await response.json();
       setRouteStopsArrivalTimes(response.data);
    }
  }, [selectedCity, selectedRoute]);

  const currentStops = useMemo(() => {
    if(!selectedRoute || !selectedRouteName){
      return [];
    }
    // 路線可能Direction只有0
    const queryDirection = stopsData.length > 1 ? currentDirection : 0;
    const stopList = stopsData.find(item => item.Direction === queryDirection)?.Stops || [];
    const arrivalTimesMap = routeStopsArrivalTimes.reduce((map, stopWithTime) => {
      map[stopWithTime.StopUID] = stopWithTime;
      return map;
    }, {});

    const updatedStops = stopList.map((rawStop) => {
      const stopWithTime = arrivalTimesMap[rawStop.StopUID];
      if (stopWithTime) {
        return {
          ...rawStop,
          StopStatus: stopWithTime.StopStatus,
          EstimateTime: Math.floor(stopWithTime.EstimateTime / 60)
        };
      }
      return rawStop;
    });

    return updatedStops;
  }, [stopsData, currentDirection, routeStopsArrivalTimes]);
  
  useEffect(() => {
    if (!selectedCity || !selectedRoute) {
      return;
    }
    fetchArrivalTimes();
  }, [selectedRoute, fetchArrivalTimes]);
  
  const getBadge = (stop) => {
    switch (stop.StopStatus) {
      case 0:
        return (
          <div className={`status ${stop.EstimateTime > 0 ? 'bg-general' : 'bg-arriving'}`}>
            {stop.EstimateTime > 0 ? (
              `${stop.EstimateTime} 分鐘`
            ) : (
              "進站中"
            )}
          </div>
        );
      case 1:
        return (
          <div className="status bg-due">
            尚未發車
          </div>
        );
      case 3:
        return (
          <div className="status bg-due">
            末班車已過
          </div>
        );
      default:
        return (
          <div className="status bg-due">
            無法獲取狀態
          </div>
        );
    }
  };

  const [showToast, setShowToast] = useState(false);

  const handleShowToast = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 10000);
  };

  return (
    <div>
      <Card className="main-card">
        <Card.Body className="search-select-container">
          <CitySelect className="search-select form-floating mb-3" onCityChange={handleCityChange} />
          <RouteSelect className="search-select form-floating mb-3" city={selectedCity} onRouteChange={handleRouteChange}
          onApiError={handleShowToast}/>
        </Card.Body>
      </Card>
     
      {selectedRouteName && (
      <Card className="main-card route-name-container">
        <Card.Body className="route-name-card">
          <div className="title-box route-name">
            <p className="label">
              路線名稱
            </p>
            <p className="name">{selectedRoute}</p>
          </div>
          <div className="title-box departure-destination">
            <p className="label">
              起點站與終點站
            </p>
            <p className="name">{selectedRouteName}
            </p>
          </div>
        </Card.Body>
      </Card>
      )}

      <div>
      <Card className="main-card route-name-container">
        <Card.Body>
          {currentStops.length > 0 ? (
            <div className="main-content">
              <div className="stop-time-container">
                <Tabs
                defaultActiveKey="profile"
                id="uncontrolled-tab-example"
                className="mb-3 direction-switch"
                activeKey={currentDirection}
                onSelect={(direction) => setCurrentDirection(Number(direction))}
                >
                  <Tab eventKey={0} title="去程" className="direction-button go">
                  </Tab>
                  <Tab eventKey={1} title="返程" className="direction-button back">
                  </Tab>
                </Tabs>     
                <ListGroup numbered variant="flush" className="stop-time">
                  <ul className="stop-list">
                    {currentStops
                    .map((stop, index) => (
                      <ListGroup.Item
                      variant="flush"
                      action
                      className="d-flex justify-content-between align-items-start"
                      key={index}
                      >
                        <div className="ms-2 me-auto stop-box">
                          <div className="stop-sequence">{stop.StopSequence}</div>
                          <div className="fw-bold stop-name">{stop.StopName.Zh_tw}</div>
                          {getBadge(stop)}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ul>
                </ListGroup>
              </div>
              <RouteShape city={selectedCity} routeName={selectedRoute}/>
            </div>
          ) : (
            <div className="empty-container" style={{ backgroundImage: `url(${emptyBg})` }}>
               <img src={caption} className="empty-caption" alt="caption" width={400}/>
            </div>
          )}
        </Card.Body>
      </Card>
      </div>
      {currentStops.length > 0 && (
      <TimeUpdate onUpdate={fetchArrivalTimes}/>
      )}

      <ToastContainer position="bottom-bottom">
      <Toast show={showToast} bg="danger">
        <Toast.Header>
         <BusAlertIcon />
          <strong className="me-auto">API請求錯誤</strong>
          <small className="text-muted">因TDX平台API有次數限制，<br/>
          若已達上限請選擇<br/>
          <strong>台北 - [307] 板橋 ↔︎ 撫遠街</strong><br/>
          將使用預存的資料進行展示</small>
        </Toast.Header>
      </Toast>
      </ToastContainer>
    </div>
  );
};

export default BusTime;
